import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { ls } from "../../../utils/localStorage";
import CustomSlider from "../../CustomSlider";
import { useNavigate, useSearchParams } from "react-router-dom";
import { buildSearchParams } from "../../../utils/searchFilters";

const getNumberParam = (params, key, fallback) => {
  const value = Number(params.get(key));
  return Number.isFinite(value) && params.has(key) ? value : fallback;
};

const SearchableDropdown = ({
  label,
  options,
  value,
  onChange,
  getOptionLabel = (option) => option?.name || "",
  disabled = false,
  noOptionsText = "No options",
}) => {
  return (
    <Autocomplete
      size="small"
      fullWidth
      options={options || []}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, selected) =>
        String(option?.id) === String(selected?.id)
      }
      disabled={disabled}
      noOptionsText={noOptionsText}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
};

const SearchByPartner = () => {
  const [searchParams] = useSearchParams();
  const [dropdownOptions, setDropdownOptions] = useState(null);
  const [age, setAge] = useState(() => [
    getNumberParam(searchParams, "age_min", 18),
    getNumberParam(searchParams, "age_max", 60),
  ]);
  const [ageFilterEnabled, setAgeFilterEnabled] = useState(
    () => searchParams.has("age_min") || searchParams.has("age_max"),
  );
  const [height, setHeight] = useState(() => [
    getNumberParam(searchParams, "height_min", 100),
    getNumberParam(searchParams, "height_max", 250),
  ]);
  const [heightFilterEnabled, setHeightFilterEnabled] = useState(
    () => searchParams.has("height_min") || searchParams.has("height_max"),
  );
  const [annualIncome, setAnnualIncome] = useState(() => [
    getNumberParam(searchParams, "annual_income_min", 5),
    getNumberParam(searchParams, "annual_income_max", 100),
  ]);
  const [incomeFilterEnabled, setIncomeFilterEnabled] = useState(
    () =>
      searchParams.has("annual_income_min") ||
      searchParams.has("annual_income_max"),
  );
  const [maritalStatus, setMaritalStatus] = useState(() =>
    searchParams.get("marital_status"),
  );
  const [motherTounge, setMotherTounge] = useState(() =>
    searchParams.get("mother_tongue"),
  );
  const [physicalStatus, setPhysicalStatus] = useState(() =>
    searchParams.get("physical_status"),
  );
  const [religion, setReligion] = useState(() =>
    searchParams.get("religion"),
  );
  const [caste, setCaste] = useState(() => searchParams.get("caste"));
  const [occupation, setOccupation] = useState(() =>
    searchParams.get("occupation"),
  );
  const [education, setEducation] = useState(() =>
    searchParams.get("education"),
  );
  const [country, setCountry] = useState(() => searchParams.get("country"));
  const [eatingHabits, setEatingHabits] = useState(() =>
    searchParams.get("eating_habit"),
  );
  const [drinkingHabits, setDrinkingHabits] = useState(() =>
    searchParams.get("drinking_habit"),
  );
  const [smokingHabits, setSmokingHabits] = useState(() =>
    searchParams.get("smoking_habit"),
  );
  const [familyStatus, setFamilyStatus] = useState(() =>
    searchParams.get("family_status"),
  );
  const [familyType, setFamilyType] = useState(() =>
    searchParams.get("family_type"),
  );
  const [userDetails, setUserDetails] = useState(null);
  const navigate = useNavigate();

  const getSelectedOptionById = (options, selectedId) => {
    if (selectedId === null || selectedId === undefined || selectedId === "") {
      return null;
    }
    return (
      options?.find((option) => String(option.id) === String(selectedId)) ||
      null
    );
  };

  const casteOptions = useMemo(() => {
    if (!religion) {
      return [];
    }

    return (dropdownOptions?.CASTE || []).filter(
      (option) => String(option.religion_id) === String(religion),
    );
  }, [religion, dropdownOptions]);

  useEffect(() => {
    const dropdownData = ls.getItem("dropdown_values_for_reference");
    const userData = ls.getItem("profile_for_reference");
    setDropdownOptions(dropdownData ? JSON.parse(dropdownData) : {});
    setUserDetails(userData ? JSON.parse(userData) : null);
  }, []);

  const handleSearch = () => {
    const filterOptions = {
      age_min: ageFilterEnabled ? age[0] : null,
      age_max: ageFilterEnabled ? age[1] : null,
      gender:
        userDetails?.gender === "1"
          ? "2"
          : userDetails?.gender === "2"
            ? "1"
            : null,
      height_min: heightFilterEnabled ? height[0] : null,
      height_max: heightFilterEnabled ? height[1] : null,
      annual_income_min: incomeFilterEnabled ? annualIncome[0] : null,
      annual_income_max: incomeFilterEnabled ? annualIncome[1] : null,
      marital_status: maritalStatus,
      mother_tongue: motherTounge,
      physical_status: physicalStatus,
      religion: religion,
      caste: caste,
      occupation: occupation,
      education: education,
      country: country,
      eating_habit: eatingHabits,
      drinking_habit: drinkingHabits,
      smoking_habit: smokingHabits,
      family_status: familyStatus,
      family_type: familyType,
      page: 1,
      limit: 20,
    };
    const searchParams = buildSearchParams(filterOptions);

    navigate(
      `/auth/search-by-partner/filtered-matches?${searchParams.toString()}`,
    );
  };

  const handleReset = () => {
    setAge([18, 60]);
    setAgeFilterEnabled(false);
    setHeight([100, 250]);
    setHeightFilterEnabled(false);
    setAnnualIncome([5, 100]);
    setIncomeFilterEnabled(false);
    setMaritalStatus(null);
    setMotherTounge(null);
    setPhysicalStatus(null);
    setReligion(null);
    setCaste(null);
    setOccupation(null);
    setEducation(null);
    setCountry(null);
    setEatingHabits(null);
    setDrinkingHabits(null);
    setSmokingHabits(null);
    setFamilyStatus(null);
    setFamilyType(null);
  };

  return (
    <Box className="">
      <Typography variant="h5" gutterBottom>
        Search by Partner
      </Typography>
      <Card sx={{ border: "1px solid #ccc", paddingY: 5, paddingX: 2, mb: 4 }}>
        <Grid container spacing={4}>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <CustomSlider
              min={18}
              max={60}
              marks
              name="age"
              value={age}
              onChange={(event, newValue) => setAge(newValue)}
              valueLabelDisplay="on"
              disabled={!ageFilterEnabled}
            />
            <Typography fontSize={12} color="textSecondary" align="center">
              Age : {age[0]} - {age[1]} years
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={ageFilterEnabled}
                  onChange={(event) =>
                    setAgeFilterEnabled(event.target.checked)
                  }
                />
              }
              label="Filter by age"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <CustomSlider
              min={100}
              max={250}
              marks
              name="height"
              value={height}
              onChange={(event, newValue) => setHeight(newValue)}
              valueLabelDisplay="on"
              disabled={!heightFilterEnabled}
            />
            <Typography fontSize={12} color="textSecondary" align="center">
              Height : {height[0]} - {height[1]} CM
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={heightFilterEnabled}
                  onChange={(event) =>
                    setHeightFilterEnabled(event.target.checked)
                  }
                />
              }
              label="Filter by height"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <CustomSlider
              min={5}
              max={100}
              marks
              name="age"
              value={annualIncome}
              onChange={(event, newValue) => setAnnualIncome(newValue)}
              valueLabelDisplay="on"
              disabled={!incomeFilterEnabled}
            />
            <Typography fontSize={12} color="textSecondary" align="center">
              Income (LPA) : {annualIncome[0]} L - {annualIncome[1]} L
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={incomeFilterEnabled}
                  onChange={(event) =>
                    setIncomeFilterEnabled(event.target.checked)
                  }
                />
              }
              label="Filter by income"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Marital Status"
              options={dropdownOptions?.MARITAL_STATUS}
              value={getSelectedOptionById(
                dropdownOptions?.MARITAL_STATUS,
                maritalStatus,
              )}
              onChange={(selected) => setMaritalStatus(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Mother Tounge"
              options={dropdownOptions?.MOTHER_TOUNGE_LIST}
              value={getSelectedOptionById(
                dropdownOptions?.MOTHER_TOUNGE_LIST,
                motherTounge,
              )}
              onChange={(selected) => setMotherTounge(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Physical Status"
              options={dropdownOptions?.PHYSICAL_STATUS}
              value={getSelectedOptionById(
                dropdownOptions?.PHYSICAL_STATUS,
                physicalStatus,
              )}
              onChange={(selected) => setPhysicalStatus(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Religion"
              options={dropdownOptions?.RELIGION}
              value={getSelectedOptionById(dropdownOptions?.RELIGION, religion)}
              onChange={(selected) => {
                const nextReligion = selected?.id || null;
                setReligion(nextReligion);
                setCaste(null);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Caste"
              options={casteOptions}
              value={getSelectedOptionById(casteOptions, caste)}
              onChange={(selected) => setCaste(selected?.id || null)}
              disabled={!dropdownOptions || !religion}
              noOptionsText={
                religion ? "No matching caste found" : "Select religion first"
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Occupation"
              options={dropdownOptions?.OCCUPATION}
              value={getSelectedOptionById(
                dropdownOptions?.OCCUPATION,
                occupation,
              )}
              onChange={(selected) => setOccupation(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Education"
              options={dropdownOptions?.EDUCATION}
              value={getSelectedOptionById(
                dropdownOptions?.EDUCATION,
                education,
              )}
              onChange={(selected) => setEducation(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Country"
              options={dropdownOptions?.COUNTRYS}
              value={getSelectedOptionById(dropdownOptions?.COUNTRYS, country)}
              onChange={(selected) => setCountry(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Eating Habits"
              options={dropdownOptions?.FOOD}
              value={getSelectedOptionById(dropdownOptions?.FOOD, eatingHabits)}
              onChange={(selected) => setEatingHabits(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Drinking Habits"
              options={dropdownOptions?.DRINKING}
              value={getSelectedOptionById(
                dropdownOptions?.DRINKING,
                drinkingHabits,
              )}
              onChange={(selected) => setDrinkingHabits(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Smoking Habits"
              options={dropdownOptions?.SMOKING}
              value={getSelectedOptionById(
                dropdownOptions?.SMOKING,
                smokingHabits,
              )}
              onChange={(selected) => setSmokingHabits(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Family Status"
              options={dropdownOptions?.FAMILY_STATUS}
              value={getSelectedOptionById(
                dropdownOptions?.FAMILY_STATUS,
                familyStatus,
              )}
              onChange={(selected) => setFamilyStatus(selected?.id || null)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SearchableDropdown
              label="Family Type"
              options={dropdownOptions?.FAMILY_TYPE}
              value={getSelectedOptionById(
                dropdownOptions?.FAMILY_TYPE,
                familyType,
              )}
              onChange={(selected) => setFamilyType(selected?.id || null)}
            />
          </Grid>
        </Grid>
        <Box sx={{ textAlign: "right" }}>
          <Button
            variant="outlined"
            color="secondary"
            sx={{ mt: 4, mr: 2 }}
            onClick={() => handleReset()}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 4 }}
            onClick={() => handleSearch()}
          >
            Search
          </Button>
        </Box>
      </Card>
    </Box>
  );
};
export default SearchByPartner;
